import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const STYLES = `
  .pcf-root {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 28px 28px 32px;
    box-sizing: border-box;
    width: 100%;
  }

  .pcf-root * { box-sizing: border-box; }

  /* ── Header ── */
  .pcf-header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 28px;
  }

  .pcf-icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(0,74,173,0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .pcf-header-text h3 {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px;
    letter-spacing: -0.01em;
  }

  .pcf-header-text p {
    font-size: 13px;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
  }

  /* ── Fields ── */
  .pcf-fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  /* ── Mobile ── */
  @media (max-width: 480px) {
    .pcf-root {
      padding: 20px 16px 24px;
      border-radius: 14px;
    }

    .pcf-header {
      gap: 12px;
      margin-bottom: 22px;
    }

    .pcf-icon-wrap {
      width: 38px;
      height: 38px;
      border-radius: 10px;
    }

    .pcf-header-text h3 {
      font-size: 16px;
    }

    .pcf-header-text p {
      font-size: 12px;
    }

    .pcf-fields {
      gap: 16px;
    }
  }

  @media (max-width: 360px) {
    .pcf-root { padding: 16px 12px 20px; }
    .pcf-header-text h3 { font-size: 15px; }
  }
`;

const PresentationContextForm = ({ formData, onChange, errors, touched, onFieldBlur }) => {
  const industryOptions = [
    { value: 'technology',    label: 'Technology & Software' },
    { value: 'finance',       label: 'Finance & Banking' },
    { value: 'healthcare',    label: 'Healthcare & Medical' },
    { value: 'education',     label: 'Education & Training' },
    { value: 'retail',        label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing & Industrial' },
    { value: 'consulting',    label: 'Consulting & Professional Services' },
    { value: 'marketing',     label: 'Marketing & Advertising' },
    { value: 'real-estate',   label: 'Real Estate & Construction' },
    { value: 'other',         label: 'Other' },
  ];

  const audienceOptions = [
    { value: 'executives', label: 'C-Level Executives' },
    { value: 'investors',  label: 'Investors & Stakeholders' },
    { value: 'clients',    label: 'Clients & Customers' },
    { value: 'team',       label: 'Internal Team' },
    { value: 'partners',   label: 'Business Partners' },
    { value: 'public',     label: 'General Public' },
    { value: 'students',   label: 'Students & Trainees' },
    { value: 'mixed',      label: 'Mixed Audience' },
  ];

  const purposeOptions = [
    { value: 'sales',      label: 'Sales Pitch' },
    { value: 'investor',   label: 'Investor Presentation' },
    { value: 'product',    label: 'Product Launch' },
    { value: 'training',   label: 'Training & Education' },
    { value: 'report',     label: 'Business Report' },
    { value: 'proposal',   label: 'Project Proposal' },
    { value: 'marketing',  label: 'Marketing Campaign' },
    { value: 'conference', label: 'Conference/Event' },
    { value: 'other',      label: 'Other' },
  ];

  const handleChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  const handleBlur = (field) => {
    if (onFieldBlur) onFieldBlur(field);
  };

  const getError = (field) => {
    if (!touched?.[field]) return '';
    const val = formData?.[field];
    if (val && String(val).trim()) return '';
    return errors?.[field] || '';
  };

  return (
    <div className="pcf-root">
      <style>{STYLES}</style>

      {/* Header */}
      <div className="pcf-header">
        <div className="pcf-icon-wrap">
          <Icon name="FileText" size={20} color="#004AAD" />
        </div>
        <div className="pcf-header-text">
          <h3>Presentation Context</h3>
          <p>Help us understand your presentation better for accurate analysis</p>
        </div>
      </div>

      {/* Fields */}
      <div className="pcf-fields">

        <Input
          type="text"
          label="Presentation Title"
          placeholder="Enter your presentation title"
          value={formData?.title}
          onChange={(e) => handleChange('title', e?.target?.value)}
          onBlur={() => handleBlur('title')}
          error={getError('title')}
          required
          description="A descriptive title for your presentation"
        />

        <Select
          label="Industry"
          placeholder="Select your industry"
          options={industryOptions}
          value={formData?.industry}
          onChange={(value) => { handleChange('industry', value); handleBlur('industry'); }}
          error={getError('industry')}
          required
          searchable
        />

        <Select
          label="Target Audience"
          placeholder="Select your target audience"
          options={audienceOptions}
          value={formData?.audience}
          onChange={(value) => { handleChange('audience', value); handleBlur('audience'); }}
          error={getError('audience')}
          required
        />

        <Select
          label="Presentation Purpose"
          placeholder="Select presentation purpose"
          options={purposeOptions}
          value={formData?.purpose}
          onChange={(value) => { handleChange('purpose', value); handleBlur('purpose'); }}
          error={getError('purpose')}
          required
        />

        <Input
          type="text"
          label="Additional Notes (Optional)"
          placeholder="Any specific aspects you'd like us to focus on..."
          value={formData?.notes}
          onChange={(e) => handleChange('notes', e?.target?.value)}
          description="Provide any additional context or specific areas of concern"
        />

      </div>
    </div>
  );
};

export default PresentationContextForm;